import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';
import { saveAs } from 'file-saver';

/**
 * Saves camera captures/recordings to the TRACI gallery album (native) or downloads (web).
 *
 * Do not stage in Directory.Cache — Android clears it later and gallery entries go blank
 * ("Aucune vignette" / "Impossible de lire la vidéo") if the MediaStore copy was incomplete.
 */
@Injectable({ providedIn: 'root' })
export class MediaSaveService {
  private static readonly ALBUM_NAME = 'TRACI';
  private static readonly STAGING_DIR = Directory.Data;
  private static readonly STAGING_FOLDER = 'traci-gallery-staging';
  private albumId: string | null = null;

  async saveBlob(blob: Blob, fileName: string): Promise<'saved' | 'downloaded'> {
    if (!blob || blob.size === 0) {
      throw new Error('Empty media blob');
    }

    if (Capacitor.isNativePlatform()) {
      await this.saveToGallery(blob, fileName);
      return 'saved';
    }
    return this.saveOnWeb(blob, fileName);
  }

  private async saveToGallery(blob: Blob, fileName: string): Promise<void> {
    const safeName = this.ensureExtension(fileName.replace(/[^\w.-]+/g, '_'), blob);
    const isVideo = blob.type.startsWith('video/') || /\.(webm|mp4|mov|mkv)$/i.test(safeName);
    const albumIdentifier = await this.resolveAlbumIdentifier();
    if (!albumIdentifier) {
      throw new Error('TRACI album is required to save media');
    }

    const stagingPath = `${MediaSaveService.STAGING_FOLDER}/${Date.now()}_${safeName}`;
    const base64 = await this.blobToBase64(blob);

    await Filesystem.writeFile({
      path: stagingPath,
      data: base64,
      directory: MediaSaveService.STAGING_DIR,
      recursive: true,
    });

    try {
      const { uri } = await Filesystem.getUri({
        path: stagingPath,
        directory: MediaSaveService.STAGING_DIR,
      });

      // Android Media plugin expects a real filesystem path (or file://), not capacitor:// URLs.
      const nativePath = this.toNativeFilesystemPath(uri);
      const baseName = this.baseNameWithoutExtension(safeName);
      const options = {
        path: nativePath.startsWith('/') ? `file://${nativePath}` : nativePath,
        fileName: baseName,
        albumIdentifier,
      };

      try {
        if (isVideo) {
          await Media.saveVideo(options);
        } else {
          await Media.savePhoto(options);
        }
      } catch (filePathError) {
        console.warn('[MediaSave] file path save failed, retrying with data URL', filePathError);
        // Data-URL path also stages under cache inside the plugin, then copies into TRACI album.
        const dataUrl = await this.blobToDataUrl(blob);
        const fallback = {
          path: dataUrl,
          fileName: baseName,
          albumIdentifier,
        };
        if (isVideo) {
          await Media.saveVideo(fallback);
        } else {
          await Media.savePhoto(fallback);
        }
      }
    } finally {
      try {
        await Filesystem.deleteFile({
          path: stagingPath,
          directory: MediaSaveService.STAGING_DIR,
        });
      } catch {
        // ignore cleanup errors
      }
    }
  }

  /**
   * Convert Capacitor / file URIs to an absolute path the Media plugin can open with `new File(...)`.
   */
  private toNativeFilesystemPath(uri: string): string {
    if (!uri) {
      return uri;
    }
    const markers = ['/_capacitor_file_', '/_capacitor_file'];
    for (const marker of markers) {
      const idx = uri.indexOf(marker);
      if (idx >= 0) {
        const rest = uri.slice(idx + marker.length);
        const path = decodeURIComponent(rest.startsWith('/') ? rest : `/${rest}`);
        return path;
      }
    }
    if (uri.startsWith('file://')) {
      return decodeURIComponent(uri.replace(/^file:\/\//, ''));
    }
    return uri;
  }

  /** Create/find the shared TRACI album used for both images and videos. */
  private async resolveAlbumIdentifier(): Promise<string> {
    if (this.albumId) {
      return this.albumId;
    }

    const albumName = MediaSaveService.ALBUM_NAME;
    const platform = Capacitor.getPlatform();

    let albumsPath = '';
    if (platform === 'android') {
      try {
        albumsPath = (await Media.getAlbumsPath()).path || '';
      } catch {
        albumsPath = '';
      }
    }

    const findAlbum = (albums: Array<{ name: string; identifier: string }>) => {
      if (platform === 'android' && albumsPath) {
        return albums.find(
          (a) => a.name === albumName && a.identifier?.startsWith(albumsPath)
        );
      }
      return albums.find((a) => a.name === albumName);
    };

    let { albums } = await Media.getAlbums();
    let existing = findAlbum(albums);
    if (!existing) {
      try {
        await Media.createAlbum({ name: albumName });
      } catch (err) {
        // Album may already exist from a race / previous session
        console.warn('[MediaSave] createAlbum:', err);
      }
      ({ albums } = await Media.getAlbums());
      existing = findAlbum(albums);
    }
    if (!existing?.identifier) {
      throw new Error(`Album "${albumName}" not found after create`);
    }

    this.albumId = existing.identifier;
    return this.albumId;
  }

  private async saveOnWeb(blob: Blob, fileName: string): Promise<'downloaded'> {
    try {
      saveAs(blob, fileName);
      return 'downloaded';
    } catch {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      return 'downloaded';
    }
  }

  private ensureExtension(fileName: string, blob: Blob): string {
    if (/\.\w{2,5}$/.test(fileName)) {
      return fileName;
    }
    if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
      return `${fileName}.jpg`;
    }
    if (blob.type.includes('png')) {
      return `${fileName}.png`;
    }
    if (blob.type.includes('mp4')) {
      return `${fileName}.mp4`;
    }
    if (blob.type.includes('webm')) {
      return `${fileName}.webm`;
    }
    if (blob.type.startsWith('video/')) {
      return `${fileName}.mp4`;
    }
    return `${fileName}.jpg`;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(blob);
    });
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(blob);
    });
  }

  private baseNameWithoutExtension(fileName: string): string {
    const safe = fileName.replace(/[^\w.-]+/g, '_');
    const dot = safe.lastIndexOf('.');
    return dot > 0 ? safe.slice(0, dot) : safe;
  }
}
